'use client'

import React, { useState, useCallback, useRef, memo } from 'react'
import { Button } from './ui/button'
import { cn } from '@/lib/utils'
import { useFocusManagement } from '@/hooks/use-accessibility'
import { useSettings } from '@/hooks/use-settings'
import { usePerformanceMonitoring } from '@/hooks/use-performance-monitoring'
import { useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts'
import { settingSections } from '@/types/settings'

// Import modular components
import SettingsSidebar from './settings/SettingsSidebar'
import AudioSettingsPanel from './settings/AudioSettingsPanel'
import VisualizationSettingsPanel from './settings/VisualizationSettingsPanel'
import InterfaceSettingsPanel from './settings/InterfaceSettingsPanel'
import PerformanceSettingsPanel from './settings/PerformanceSettingsPanel'
import AdvancedSettingsPanel from './settings/AdvancedSettingsPanel'

interface SettingsPanelProps {
  isOpen: boolean
  onClose: () => void
}

const SettingsPanel = memo<SettingsPanelProps>(({ isOpen, onClose }) => {
  // RAW STATE MANAGEMENT
  const [activeSection, setActiveSection] = useState('audio')
  const panelRef = useRef<HTMLDivElement>(null)

  // Custom hooks for functionality
  const {
    settings,
    hasUnsavedChanges,
    handleSettingChange,
    handleResetSection,
    handleResetAll,
    handleExport,
    handleLoadPreset,
    handleImport
  } = useSettings()

  const performanceMetrics = usePerformanceMonitoring(isOpen)

  // ACCESSIBILITY - Focus Management
  const { containerRef } = useFocusManagement({
    autoFocus: isOpen,
    restoreFocus: true,
    trapFocus: isOpen,
    initialFocusRef: panelRef
  })

  // Sync containerRef with panelRef for focus management
  React.useEffect(() => {
    if (panelRef.current && containerRef.current !== panelRef.current) {
      // Use type assertion to bypass read-only restriction
      (containerRef as any).current = panelRef.current
    }
  }, [containerRef])

  // KEYBOARD NAVIGATION - Modal Controls
  React.useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  // Keyboard shortcuts
  useKeyboardShortcuts(isOpen, settings, {
    onExport: handleExport,
    onResetAll: handleResetAll,
    onClose
  })

  // RENDER ACTIVE SECTION CONTENT
  const renderActiveSection = useCallback(() => {
    const commonProps = {
      settings,
      onSettingChange: handleSettingChange,
      onResetSection: handleResetSection
    }

    switch (activeSection) {
      case 'audio':
        return (
          <AudioSettingsPanel
            {...commonProps}
            performanceMetrics={performanceMetrics}
          />
        )
      case 'visualization':
        return <VisualizationSettingsPanel {...commonProps} />
      case 'interface':
        return <InterfaceSettingsPanel {...commonProps} />
      case 'performance':
        return (
          <PerformanceSettingsPanel
            {...commonProps}
            performanceMetrics={performanceMetrics}
          />
        )
      case 'advanced':
        return (
          <AdvancedSettingsPanel
            {...commonProps}
            onResetAll={handleResetAll}
            onExport={handleExport}
            onImport={handleImport}
          />
        )
      default:
        return (
          <AudioSettingsPanel
            {...commonProps}
            performanceMetrics={performanceMetrics}
          />
        )
    }
  }, [activeSection, settings, performanceMetrics, handleSettingChange, handleResetSection, handleResetAll, handleExport, handleImport])

  if (!isOpen) return null

  // ULTRA-POWERFUL RENDER - MAXIMUM DOMINATION
  return (
    <div
      ref={panelRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-title"
      aria-describedby="settings-description"
    >
      <div className="w-full max-w-7xl h-[98vh] bg-slate-900/98 backdrop-blur-md rounded-2xl border border-slate-700/60 shadow-2xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-300">
        {/* ULTRA HEADER - RAW POWER STATUS */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700/50 bg-gradient-to-r from-slate-900/50 to-slate-800/50">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/30">
              <span className="text-2xl font-bold">SET</span>
            </div>
            <div>
              <h2 id="settings-title" className="text-2xl font-bold text-white">
                Sound Wizard Studio - Settings
              </h2>
              <p id="settings-description" className="text-slate-400 text-sm">
                Maximum Power Configuration Engine
                {hasUnsavedChanges && <span className="text-orange-400 ml-2">● Unsaved Changes</span>}
              </p>
            </div>
          </div>

          {/* PERFORMANCE INDICATOR */}
          <div className="flex items-center space-x-4">
            <div className="hidden md:flex items-center space-x-3 text-xs">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-green-400 font-mono">{performanceMetrics.fps}fps</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
                <span className="text-cyan-400 font-mono">{performanceMetrics.renderTime}ms</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                <span className="text-purple-400 font-mono">{performanceMetrics.memoryUsage}MB</span>
              </div>
            </div>

            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-lg transition-all duration-200"
              aria-label="Close settings panel"
            >
              <span className="text-xl">✕</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row h-full">
          {/* SIDEBAR */}
          <SettingsSidebar
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            onLoadPreset={handleLoadPreset}
          />

          {/* ULTRA CONTENT AREA - RAW SETTINGS POWER */}
          <div className="flex-1 overflow-y-auto bg-slate-900/20 lg:ml-0 custom-scrollbar scroll-smooth panel-content">
            <div className="p-6">
              <div className="space-y-6">
              {/* ULTRA SECTION HEADER */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-1">
                    {settingSections.find(s => s.id === activeSection)?.title}
                  </h3>
                  <p className="text-slate-400 text-sm">
                    {settingSections.find(s => s.id === activeSection)?.description}
                  </p>
                </div>

                {/* SECTION STATUS */}
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-xs text-green-400 font-mono">ACTIVE</span>
                </div>
              </div>

              {/* ULTRA SETTINGS CONTENT */}
              <div role="tabpanel" id="settings-panel" className="animate-in fade-in-0 slide-in-from-right-5 duration-300">
                {renderActiveSection()}
              </div>
              </div>
            </div>
          </div>
        </div>

        {/* ULTRA FOOTER - RAW POWER CONTROLS */}
        <div className="flex items-center justify-between p-6 border-t border-slate-700/50 bg-slate-900/50">
          <div className="flex items-center space-x-4">
            <div className="text-xs text-slate-400">
              Auto-saved • {hasUnsavedChanges ? 'Unsaved changes' : 'All changes saved'}
            </div>
            {settings.keyboardShortcuts && (
              <div className="text-xs text-slate-400 bg-slate-800/50 px-2 py-1 rounded">
                Shortcuts enabled
              </div>
            )}
          </div>

          <div className="flex space-x-3">
            <Button
              onClick={handleResetAll}
              variant="outline"
              size="sm"
              className="px-4 py-2 border-red-600/50 text-red-400 hover:bg-red-600/10 hover:border-red-500"
            >
              Reset All
            </Button>
            <Button
              onClick={handleExport}
              variant="outline"
              size="sm"
              className="px-4 py-2 border-purple-600/50 text-purple-400 hover:bg-purple-600/10 hover:border-purple-500"
            >
              Export
            </Button>
            <Button
              onClick={onClose}
              className="px-6 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 shadow-lg shadow-cyan-500/30 hover:shadow-xl hover:shadow-cyan-500/40 transition-all duration-200"
            >
              Apply & Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
})

SettingsPanel.displayName = 'SettingsPanel'

export default SettingsPanel